import { useMemo, useState, useEffect } from 'react';
import type { FunctionComponent, ChangeEvent, ReactNode } from 'react';

import { ComboBox, menuHelpers, useI18n } from '@pega/cosmos-react-core';
import type {
  ComboBoxProps,
  NoChildrenProp,
  FormControlProps,
  MenuItemProps,
  MenuProps
} from '@pega/cosmos-react-core';

import type { EmailComposerProps, EmailUser } from './Email.types';

export interface EmailSelectorProps extends NoChildrenProp, Pick<MenuProps, 'loading' | 'accent'> {
  /** participants */
  participants: EmailComposerProps['participants'];
  /** default selections */
  selectedItems: EmailUser['emailAddress'][];
  /** selectedItems */
  onSelectedItemChange: (selectedItems: EmailUser['emailAddress'][], label: ReactNode) => void;
  /** Callback fired when the Combobox input value changes. */
  onFilterChange: (filterValue: string) => void;
  /** Callback to fetch more rows */
  onLoadMore?: (filterValue: string) => void;
  /**
   * Callback to handle external entry validation
   * If returned true considers the entry to be a valid entry
   */
  externalValidator?: EmailComposerProps['externalValidator'];
  status?: FormControlProps['status'];
  info?: FormControlProps['info'];
  compose?: boolean;
  comboBoxHandle?: ComboBoxProps['handle'];
}

const EmailSelector: FunctionComponent<
  EmailSelectorProps & Pick<ComboBoxProps, 'label' | 'mode' | 'required'>
> = ({
  participants,
  selectedItems,
  label,
  mode,
  required,
  onSelectedItemChange,
  externalValidator,
  status,
  info,
  compose,
  onFilterChange,
  onLoadMore,
  loading,
  accent,
  comboBoxHandle
}) => {
  const [filterValue, setFilterValue] = useState('');
  const [customEmailParticipants, setCustomEmailParticipants] = useState<
    EmailSelectorProps['participants']
  >([]);
  const t = useI18n();

  useEffect(() => {
    onFilterChange(filterValue);
  }, [filterValue]);

  const emailsToRender = useMemo(() => {
    const emailParticipants: EmailSelectorProps['participants'] =
      participants.concat(customEmailParticipants);

    const items = emailParticipants.map(({ emailAddress, fullName }) => {
      return {
        id: emailAddress,
        primary: fullName,
        secondary: [emailAddress],
        selected: selectedItems.includes(emailAddress)
      };
    });

    const isValid = externalValidator?.(filterValue);

    return ((filterValue && externalValidator && isValid) || (filterValue && !externalValidator)) &&
      !compose
      ? [
          {
            id: filterValue,
            primary: `${t('use')}: ${filterValue}`,
            selected: selectedItems.includes(filterValue)
          },
          ...items
        ]
      : items;
  }, [participants, customEmailParticipants, selectedItems]);

  const toggleSelectedItems = (id: string) => {
    let newSelectedItems = mode === 'single-select' ? [] : [...selectedItems];

    if (selectedItems.includes(id)) {
      // Unselect an already selected item
      newSelectedItems = newSelectedItems.filter(value => value !== id);
    } else {
      setFilterValue('');
      const filteredItem = menuHelpers.getItem<MenuItemProps>(emailsToRender, id);

      if (!filteredItem?.secondary) {
        // New value addition to the list
        if (!externalValidator) {
          setCustomEmailParticipants([
            ...customEmailParticipants,
            {
              shortName: '',
              fullName: '',
              emailAddress: filterValue
            }
          ]);
          newSelectedItems = [...newSelectedItems, id];
        } else {
          const isValid = externalValidator?.(filterValue);
          if (isValid) {
            setCustomEmailParticipants([
              ...customEmailParticipants,
              {
                shortName: '',
                fullName: '',
                emailAddress: filterValue
              }
            ]);
            newSelectedItems = [...newSelectedItems, id];
          }
        }
      } else {
        // Existing value to be selected
        newSelectedItems = [...newSelectedItems, id];
      }
    }

    onSelectedItemChange(newSelectedItems, label);
  };

  const getSelectedItems = () => {
    if (mode === 'single-select') {
      return { id: selectedItems[0], text: selectedItems[0] };
    }
    return selectedItems.map(value => ({ id: value, text: value }));
  };

  const comboBoxProps = {
    value: filterValue,
    onChange: (e: ChangeEvent<HTMLInputElement>) => {
      if (mode === 'single-select' && !e.target.value) {
        onSelectedItemChange([], label);
      }
      setFilterValue(e.target.value);
    },
    onBlur: () => {
      setFilterValue('');
    },
    menu: {
      items: emailsToRender,
      accent,
      onItemClick: toggleSelectedItems,
      loadMore: () => {
        onLoadMore?.(filterValue);
      },
      loading
    },
    selected: {
      items: getSelectedItems(),
      onRemove: toggleSelectedItems
    },
    label,
    mode,
    required,
    status,
    info,
    handle: comboBoxHandle
  };

  return <ComboBox {...comboBoxProps} />;
};

export default EmailSelector;
